/** Shared output helpers: human tables by default, `--json` for scripting. */

export interface OutputOptions {
  json?: boolean
}

export function printJson(value: unknown): void {
  console.log(JSON.stringify(value, null, 2))
}

/** Render rows as an aligned column table. Columns are picked from `columns` (key → header). */
export function printTable<T extends Record<string, unknown>>(rows: T[], columns: Record<string, string>): void {
  if (rows.length === 0) {
    console.log('(none)')
    return
  }
  const keys = Object.keys(columns)
  const cells = rows.map((row) => keys.map((key) => formatCell(row[key])))
  const headers = keys.map((key) => columns[key])
  const widths = headers.map((header, i) => Math.max(header.length, ...cells.map((row) => row[i].length)))
  const line = (parts: string[]) => parts.map((part, i) => part.padEnd(widths[i])).join('  ').trimEnd()
  console.log(line(headers))
  console.log(line(widths.map((w) => '-'.repeat(w))))
  for (const row of cells) console.log(line(row))
}

function formatCell(value: unknown): string {
  if (value === null || value === undefined) return ''
  if (value instanceof Date) return value.toISOString()
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

/** Print either JSON (with --json) or a table of the given rows. */
export function output<T extends Record<string, unknown>>(rows: T[], columns: Record<string, string>, options: OutputOptions, jsonValue?: unknown): void {
  if (options.json) {
    printJson(jsonValue ?? rows)
    return
  }
  printTable(rows, columns)
}

/** Print a single record as `key: value` lines, or JSON with --json. */
export function outputRecord(record: Record<string, unknown>, options: OutputOptions): void {
  if (options.json) {
    printJson(record)
    return
  }
  const width = Math.max(...Object.keys(record).map((k) => k.length))
  for (const [key, value] of Object.entries(record)) {
    console.log(`${key.padEnd(width)}  ${formatCell(value)}`)
  }
}
